import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiMail, FiEye, FiCheckCircle, FiTrendingUp, FiUserX, FiRefreshCw, FiAlertTriangle, FiHelpCircle, FiMousePointer, FiMessageCircle, FiSend, FiX, FiArrowLeft } from 'react-icons/fi';
import axios from 'axios';
import toast from 'react-hot-toast';
import AppHeader from './AppHeader';
import AICampaignAnalysis from './AICampaignAnalysis';
import { getValidAccessToken } from '../utils/microsoftAuth';
import { handleSessionExpired } from '../utils/auth';

// Reusable stat card with a clean white design
const StatCard = ({ label, value, sub, icon: Icon, iconBg, iconColor }) => (
  <div className="bg-white rounded-xl border border-neutral-200 shadow-soft p-6 hover:shadow-medium transition-all duration-200">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-neutral-500">{label}</p>
        <p className="text-3xl font-bold text-neutral-900 mt-1">{value}</p>
        {sub && <p className="text-xs text-neutral-400 mt-1">{sub}</p>}
      </div>
      <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center`}>
        <Icon className={`w-6 h-6 ${iconColor}`} />
      </div>
    </div>
  </div>
);

const PROVIDER_LABELS = { all: 'All', microsoft: '📧 Microsoft', gmail: '✉️ Gmail' };

const TrackingDashboard = () => {
  const [stats, setStats] = useState(null);
  const [trackingData, setTrackingData] = useState([]);
  const [unsubscribeStats, setUnsubscribeStats] = useState(null);
  const [unsubscribeData, setUnsubscribeData] = useState([]);
  const [unsubscribeList, setUnsubscribeList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('emails');
  const [queueItems, setQueueItems] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(false);
  // Provider filter — default to the last logged-in provider stored in localStorage
  const [providerFilter, setProviderFilter] = useState(
    () => localStorage.getItem('active_provider') || 'all'
  );
  const userEmail = (localStorage.getItem('ms_user_email') || '').toLowerCase();
  const userRole = localStorage.getItem('ms_user_role') || 'user';
  const isAdmin = userRole === 'admin';

  // Reply thread modal state
  const [threadModal, setThreadModal] = useState(null); // { to_email, subject, provider, messages, loading }
  const [replyBody, setReplyBody] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [showReplyConfirm, setShowReplyConfirm] = useState(false);
  const [pendingReplyQueueId, setPendingReplyQueueId] = useState(null);
  const [pendingReplyRecalled, setPendingReplyRecalled] = useState(false);
  const [replyCountdown, setReplyCountdown] = useState(60);
  const [recallingReplyId, setRecallingReplyId] = useState(null);
  const replyInFlightRef = useRef(false);

  useEffect(() => {
    loadStats();
    loadTrackingData();
    loadUnsubscribeStats();
    loadUnsubscribeData();
    loadUnsubscribeList();
    loadQueueItems();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadStats();
      loadTrackingData();
      loadUnsubscribeStats();
      loadUnsubscribeData();
      loadQueueItems();
    }, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerFilter]);

  const loadQueueItems = async () => {
    setLoadingQueue(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      // Queue/recalls are user-isolated for everyone, including admins.
      if (userEmail) params.append('user', userEmail);
      const response = await axios.get(`/api/scheduler/queue?${params.toString()}`);
      setQueueItems(response.data.items || []);
    } catch (error) {
      console.error('Error loading queue items:', error);
    } finally {
      setLoadingQueue(false);
    }
  };

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    if (providerFilter !== 'all') params.append('provider', providerFilter);
    if (userEmail) params.append('user', userEmail);
    params.append('role', userRole);
    return params.toString();
  };

  const loadStats = async () => {
    try {
      const query = buildQueryParams();
      const response = await axios.get(`/api/tracking/stats${query ? `?${query}` : ''}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadTrackingData = async () => {
    try {
      const params = new URLSearchParams({ limit: '50' });
      const extra = buildQueryParams();
      if (extra) extra.split('&').forEach((p) => {
        const [k, v] = p.split('=');
        params.append(k, v);
      });
      const response = await axios.get(`/api/tracking?${params.toString()}`);
      setTrackingData(response.data);
    } catch (error) {
      console.error('Error loading tracking data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnsubscribeStats = async () => {
    try {
      const response = await axios.get('/api/tracking/unsubscribe/stats');
      setUnsubscribeStats(response.data);
    } catch (error) {
      console.error('Error loading unsubscribe stats:', error);
    }
  };

  const loadUnsubscribeData = async () => {
    try {
      const response = await axios.get('/api/tracking/unsubscribe/history?limit=50');
      setUnsubscribeData(response.data);
    } catch (error) {
      console.error('Error loading unsubscribe data:', error);
    }
  };

  const loadUnsubscribeList = async () => {
    try {
      const response = await axios.get('/api/unsubscribe/list');
      setUnsubscribeList(response.data);
    } catch (error) {
      console.error('Error loading unsubscribe list:', error);
    }
  };

  const isEmailUnsubscribed = (email) => {
    return unsubscribeList.some(item => item.email.toLowerCase() === email.toLowerCase());
  };

  // ── Reply thread logic ─────────────────────────────────────────────
  const getAuthHeaders = useCallback(async (providerOverride) => {
    const provider = providerOverride || localStorage.getItem('active_provider') || 'microsoft';
    if (provider === 'gmail') {
      const token = localStorage.getItem('google_access_token');
      return token ? { Authorization: `Bearer ${token}` } : {};
    }
    const token = await getValidAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const openThreadModal = async (item) => {
    const provider = item.provider || localStorage.getItem('active_provider') || 'microsoft';
    setThreadModal({ to_email: item.to_email, subject: item.subject, provider, messages: [], loading: true });
    setReplyBody('');
    try {
      const { data } = await axios.get('/api/inbox/thread-by-email', {
        headers: await getAuthHeaders(provider),
        params: { provider, email: item.to_email, subject: item.subject },
      });
      setThreadModal((prev) => prev ? { ...prev, messages: data.messages || [], threadId: data.threadId, loading: false } : null);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message;
      if (e?.response?.status === 401) {
        handleSessionExpired('Your email session has expired. Please sign in again.');
      } else {
        toast.error(`Could not load thread: ${msg}`);
      }
      setThreadModal((prev) => prev ? { ...prev, loading: false } : null);
    }
  };

  const handleSendReply = (e) => {
    e.preventDefault();
    if (!replyBody.trim() || !threadModal || threadModal.messages.length === 0) return;
    setShowReplyConfirm(true);
  };

  const handleConfirmReply = async () => {
    if (replyInFlightRef.current) return;
    replyInFlightRef.current = true;
    setShowReplyConfirm(false);
    if (!replyBody.trim() || !threadModal || threadModal.messages.length === 0) {
      replyInFlightRef.current = false;
      return;
    }
    setSendingReply(true);
    const lastMsg = threadModal.messages[threadModal.messages.length - 1];
    const replyText = replyBody.trim();
    try {
      const token = threadModal.provider === 'gmail'
        ? localStorage.getItem('google_access_token')
        : (await getValidAccessToken() || '');
      if (!token) {
        setSendingReply(false);
        replyInFlightRef.current = false;
        return; // handleSessionExpired already triggered inside refreshAccessToken
      }
      const sendAfter = new Date(Date.now() + 60 * 1000).toISOString();
      const { data } = await axios.post('/api/scheduler/queue', {
        from_email: userEmail,
        recipients: [extractEmail(threadModal.to_email)],
        subject: threadModal.subject,
        body: replyText,
        provider: threadModal.provider,
        access_token: token,
        send_after: sendAfter,
        reply_to_message_id: lastMsg.id,
        reply_to_thread_id: threadModal.threadId,
      }, { headers: await getAuthHeaders(threadModal.provider) });
      const queueId = data.id;
      setPendingReplyQueueId(queueId);
      setPendingReplyRecalled(false);
      setReplyBody('');
      toast.success('Reply queued — you can recall it for 60 seconds.');
      // Show pending reply in the thread
      const pendingMsg = {
        id: `pending-${queueId}`,
        from: userEmail,
        to: extractEmail(threadModal.to_email),
        date: new Date().toISOString(),
        body: `<p>${replyText.replace(/\n/g, '<br/>')}</p>`,
        pending: true,
        queueId,
      };
      setThreadModal((prev) => prev ? { ...prev, messages: [...(prev.messages || []).filter((m) => !(m.pending && m.body === pendingMsg.body)), pendingMsg] } : null);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message;
      toast.error(`Failed to queue reply: ${msg}`);
      console.error('queueReply error:', e);
    } finally {
      setSendingReply(false);
      replyInFlightRef.current = false;
    }
  };

  const handleRecallPendingReply = async () => {
    if (!pendingReplyQueueId) return;
    setRecallingReplyId(pendingReplyQueueId);
    try {
      await axios.post(`/api/scheduler/queue/${pendingReplyQueueId}/recall`);
      setPendingReplyRecalled(true);
      toast.success('Reply recalled successfully');
      setThreadModal((prev) => prev ? { ...prev, messages: (prev.messages || []).filter((m) => m.queueId !== pendingReplyQueueId) } : null);
      setPendingReplyQueueId(null);
    } catch (e) {
      toast.error('Could not recall reply: ' + (e.response?.data?.error || e.message));
    } finally {
      setRecallingReplyId(null);
    }
  };

  // Live countdown for pending reply recall window
  useEffect(() => {
    if (!pendingReplyQueueId || pendingReplyRecalled) {
      setReplyCountdown(60);
      return;
    }
    setReplyCountdown(60);
    const interval = setInterval(() => {
      setReplyCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [pendingReplyQueueId, pendingReplyRecalled]);

  // Watch pending reply and refresh thread once it is actually sent
  // openThreadModal is intentionally omitted: we only want to restart polling when the queue id changes.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!pendingReplyQueueId || pendingReplyRecalled) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`/api/scheduler/queue/${pendingReplyQueueId}`, { headers: await getAuthHeaders(threadModal?.provider) });
        if (data.status === 'sent' || data.status === 'failed') {
          setPendingReplyQueueId(null);
          setThreadModal((prev) => prev ? { ...prev, messages: (prev.messages || []).filter((m) => m.queueId !== pendingReplyQueueId) } : null);
          if (threadModal) openThreadModal({ to_email: threadModal.to_email, subject: threadModal.subject, provider: threadModal.provider });
          if (data.status === 'failed') {
            toast.error(`Reply failed: ${data.error || 'Unknown error'}`);
          }
        }
      } catch (e) {
        console.error('pending reply poll error:', e);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [pendingReplyQueueId, pendingReplyRecalled, threadModal, getAuthHeaders]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const formatMsgDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const extractName = (str) => {
    const match = (str || '').match(/^(.+?)\s*</);
    return match ? match[1].trim().replace(/^["']|["']$/g, '') : (str || '').replace(/<.*>/, '').trim();
  };

  const extractEmail = (str) => {
    const match = (str || '').match(/<([^>]+)>/);
    return match ? match[1].toLowerCase() : (str || '').toLowerCase();
  };

  const handleRecall = async (id) => {
    try {
      await axios.post(`/api/scheduler/queue/${id}/recall`);
      toast.success('Email recalled successfully');
      loadQueueItems();
    } catch (error) {
      toast.error('Could not recall: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleResubscribe = async (email) => {
    if (!window.confirm(`Are you sure you want to resubscribe ${email}?`)) return;
    try {
      await axios.delete(`/api/unsubscribe/${encodeURIComponent(email)}`);
      toast.success(`${email} has been resubscribed`);
      loadUnsubscribeStats();
      loadUnsubscribeData();
      loadUnsubscribeList();
    } catch (error) {
      toast.error('Error resubscribing: ' + (error.response?.data?.error || error.message));
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 pb-12">
      <AppHeader />

      {/* Main Content */}
      <div className="w-full max-w-[1600px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">Email Tracking Dashboard</h2>
            <p className="text-sm text-neutral-500">Monitor email delivery, opens, and engagement</p>
          </div>
          <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1">
            {Object.entries(PROVIDER_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => { setLoading(true); setProviderFilter(key); }}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 ${
                  providerFilter === key
                    ? 'bg-white text-primary-700 shadow-soft'
                    : 'text-neutral-500 hover:text-neutral-800'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-soft border border-neutral-200 overflow-hidden">
          <div className="p-8">

            {/* Tabs */}
            <div className="flex gap-1 mb-8 border-b border-neutral-200">
              <button
                onClick={() => setActiveTab('emails')}
                className={`px-6 py-3 font-semibold text-sm transition-all duration-200 relative ${
                  activeTab === 'emails' ? 'text-primary-600' : 'text-neutral-500 hover:text-neutral-800'
                }`}
              >
                Email Tracking
                {activeTab === 'emails' && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 rounded-full"></span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('unsubscribes')}
                className={`px-6 py-3 font-semibold text-sm transition-all duration-200 relative ${
                  activeTab === 'unsubscribes' ? 'text-neutral-900' : 'text-neutral-500 hover:text-neutral-800'
                }`}
              >
                Unsubscribe Tracking
                {activeTab === 'unsubscribes' && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-neutral-800 rounded-full"></span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('recent-sends')}
                className={`px-6 py-3 font-semibold text-sm transition-all duration-200 relative ${
                  activeTab === 'recent-sends' ? 'text-red-600' : 'text-neutral-500 hover:text-neutral-800'
                }`}
              >
                Recent Sends / Recall
                {activeTab === 'recent-sends' && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-600 rounded-full"></span>
                )}
              </button>
            </div>

            {/* Email Statistics */}
            {activeTab === 'emails' && stats && (
              <div>
                {/* All stats in a responsive grid — fills wider screens better */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-6">
                  <StatCard label="Total Sent"          value={stats.total || 0}            icon={FiMail}          iconBg="bg-primary-50"  iconColor="text-primary-600" />
                  <StatCard label="Delivered"           value={stats.delivered || 0}        sub={`${stats.deliveryRate || 0}% delivery rate`}     icon={FiCheckCircle}   iconBg="bg-success-50"  iconColor="text-success-600" />
                  <StatCard label="Opened"              value={stats.opened || 0}           sub={`${stats.openRate || 0}% open rate`}             icon={FiEye}           iconBg="bg-blue-50"     iconColor="text-blue-600" />
                  <StatCard label="Not Opened"          value={stats.notOpened || 0}        sub={`${stats.delivered > 0 ? Math.round(((stats.notOpened || 0) / stats.delivered) * 100) : 0}% of delivered`} icon={FiMail} iconBg="bg-red-50" iconColor="text-red-500" />
                  <StatCard label="Total Clicks"        value={stats.totalClicks || 0}      sub={`${stats.clickRate || 0}% click rate`}            icon={FiMousePointer}  iconBg="bg-indigo-50"   iconColor="text-indigo-600" />
                  <StatCard label="Total Opens"         value={stats.totalOpens || 0}       sub={`Avg ${stats.avgOpens || 0} opens/email`}         icon={FiTrendingUp}    iconBg="bg-purple-50"   iconColor="text-purple-600" />
                  <StatCard label="Inbox (Estimated)"   value={stats.inbox || 0}            sub={`${stats.inboxRate || 0}% inbox rate`}            icon={FiCheckCircle}   iconBg="bg-success-50"  iconColor="text-success-600" />
                  <StatCard label="Spam (Estimated)"    value={stats.spam || 0}             sub={`${stats.spamRate || 0}% spam rate`}              icon={FiAlertTriangle} iconBg="bg-amber-50"    iconColor="text-amber-600" />
                  <StatCard label="Unknown Placement"   value={stats.unknownPlacement || 0} sub="Placement pending"                                icon={FiHelpCircle}    iconBg="bg-neutral-100" iconColor="text-neutral-500" />
                </div>

                {/* AI Campaign Analysis Panel */}
                <div className="mb-6">
                  <AICampaignAnalysis stats={stats} />
                </div>

                {/* Unsubscribe note */}
                {unsubscribeStats && unsubscribeStats.pageViews > 0 && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>ℹ️ Note:</strong> Unsubscribe page views ({unsubscribeStats.pageViews}) are tracked separately.{' '}
                      <button
                        onClick={() => setActiveTab('unsubscribes')}
                        className="text-blue-700 underline hover:text-blue-900 font-semibold"
                      >
                        View Unsubscribe Tracking
                      </button>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Unsubscribe Statistics */}
            {activeTab === 'unsubscribes' && unsubscribeStats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <StatCard label="Total Unsubscribed"  value={unsubscribeStats.totalUnsubscribed || 0}  icon={FiUserX}       iconBg="bg-red-50"     iconColor="text-red-500" />
                <StatCard label="Page Views"          value={unsubscribeStats.pageViews || 0}          sub="Unsubscribe page visits" icon={FiEye} iconBg="bg-amber-50"   iconColor="text-amber-600" />
                <StatCard label="Confirmed Actions"   value={unsubscribeStats.actualUnsubscribes || 0} icon={FiCheckCircle} iconBg="bg-neutral-100" iconColor="text-neutral-600" />
              </div>
            )}

            {/* Email Tracking Table */}
            {activeTab === 'emails' && (
              <div className="overflow-x-auto">
                <h2 className="text-lg font-bold text-neutral-900 mb-4">Recent Email Activity</h2>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                  </div>
                ) : trackingData.length === 0 ? (
                  <div className="text-center py-12 bg-neutral-50 rounded-xl border border-neutral-200">
                    <div className="w-14 h-14 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <FiMail className="w-7 h-7 text-neutral-400" />
                    </div>
                    <p className="text-neutral-500 font-medium">No tracking data yet</p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-neutral-200 overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-neutral-50 border-b border-neutral-200">
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">To</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Subject</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Provider</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Status</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Delivered</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Opened</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Opens</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Placement</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Sent At</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Replies</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trackingData.map((item) => (
                          <tr key={item.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                            <td className="py-4 px-4 text-neutral-700 text-sm font-medium">{item.to_email}</td>
                            <td className="py-4 px-4 text-neutral-700 text-sm">{item.subject}</td>
                            <td className="py-4 px-4">
                              <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                                item.provider === 'gmail'
                                  ? 'bg-red-50 text-red-700'
                                  : 'bg-blue-50 text-blue-700'
                              }`}>
                                {item.provider === 'gmail' ? '✉️ Gmail' : '📧 Microsoft'}
                              </span>
                            </td>
                            <td className="py-4 px-4">
                              <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                                item.status === 'opened'    ? 'bg-success-50 text-success-700' :
                                item.status === 'delivered' ? 'bg-primary-50 text-primary-700' :
                                'bg-neutral-100 text-neutral-600'
                              }`}>
                                {item.status}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-neutral-500 text-sm">
                              {item.delivered_at ? new Date(item.delivered_at).toLocaleString() : '—'}
                            </td>
                            <td className="py-4 px-4 text-neutral-500 text-sm">
                              {item.opened_at ? new Date(item.opened_at).toLocaleString() : 'Not opened'}
                            </td>
                            <td className="py-4 px-4">
                              <span className="font-semibold text-neutral-700">{item.open_count || 0}</span>
                            </td>
                            <td className="py-4 px-4">
                              <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                                item.placement_status === 'inbox' ? 'bg-success-50 text-success-700' :
                                item.placement_status === 'spam'  ? 'bg-amber-50 text-amber-700' :
                                'bg-neutral-100 text-neutral-500'
                              }`}>
                                {item.placement_status || 'unknown'}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-neutral-500 text-sm">
                              {new Date(item.sent_at).toLocaleString()}
                            </td>
                            <td className="py-4 px-4">
                              <button
                                onClick={() => openThreadModal(item)}
                                className="px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-all duration-200 text-xs flex items-center gap-1.5 font-semibold"
                                title="View reply thread"
                              >
                                <FiMessageCircle className="w-3.5 h-3.5" />
                                View Thread
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Recent Sends / Recall Table */}
            {activeTab === 'recent-sends' && (
              <div className="overflow-x-auto">
                <h2 className="text-lg font-bold text-neutral-900 mb-4">Recent Sends — Recall within the window</h2>
                {loadingQueue ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600"></div>
                  </div>
                ) : queueItems.length === 0 ? (
                  <div className="text-center py-12 bg-neutral-50 rounded-xl border border-neutral-200">
                    <div className="w-14 h-14 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <FiSend className="w-7 h-7 text-neutral-400" />
                    </div>
                    <p className="text-neutral-500 font-medium">No recent queued sends</p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-neutral-200 overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-neutral-50 border-b border-neutral-200">
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">From</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Recipients</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Subject</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Provider</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Status</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Send After</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {queueItems.map((item) => {
                          const isQueued = item.status === 'queued' || item.status === 'pending';
                          const isSending = item.status === 'sending';
                          return (
                            <tr key={item.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                              <td className="py-4 px-4 text-neutral-700 text-sm font-medium">{item.from_email}</td>
                              <td className="py-4 px-4 text-neutral-700 text-sm">
                                {(item.recipients || []).length} recipient(s)
                              </td>
                              <td className="py-4 px-4 text-neutral-700 text-sm">{item.subject}</td>
                              <td className="py-4 px-4">
                                <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                                  item.provider === 'gmail' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
                                }`}>
                                  {item.provider === 'gmail' ? '✉️ Gmail' : '📧 Microsoft'}
                                </span>
                              </td>
                              <td className="py-4 px-4">
                                <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                                  item.status === 'sent' ? 'bg-success-50 text-success-700' :
                                  item.status === 'failed' ? 'bg-red-50 text-red-700' :
                                  item.status === 'recalled' ? 'bg-neutral-100 text-neutral-600' :
                                  'bg-amber-50 text-amber-700'
                                }`}>
                                  {item.status}
                                </span>
                              </td>
                              <td className="py-4 px-4 text-neutral-500 text-sm">
                                {item.send_after ? new Date(item.send_after).toLocaleString() : '—'}
                              </td>
                              <td className="py-4 px-4">
                                {(isQueued || isSending) && (
                                  <button
                                    onClick={() => handleRecall(item.id)}
                                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 text-xs flex items-center gap-1.5 font-semibold shadow-soft"
                                    title="Recall this email before it is sent"
                                  >
                                    <FiX className="w-3.5 h-3.5" />
                                    Recall
                                  </button>
                                )}
                                {item.status === 'recalled' && (
                                  <span className="text-xs text-neutral-500">Recalled</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Unsubscribe Tracking Table */}
            {activeTab === 'unsubscribes' && (
              <div className="overflow-x-auto">
                <h2 className="text-lg font-bold text-neutral-900 mb-4">Unsubscribe Activity</h2>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-neutral-600"></div>
                  </div>
                ) : unsubscribeData.length === 0 ? (
                  <div className="text-center py-12 bg-neutral-50 rounded-xl border border-neutral-200">
                    <div className="w-14 h-14 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <FiUserX className="w-7 h-7 text-neutral-400" />
                    </div>
                    <p className="text-neutral-500 font-medium">No unsubscribe data yet</p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-neutral-200 overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-neutral-50 border-b border-neutral-200">
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Email</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Page Viewed</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">View Count</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Unsubscribed At</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Reason</th>
                          <th className="text-left py-3.5 px-4 font-semibold text-neutral-600 text-sm">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unsubscribeData.map((item) => (
                          <tr key={item.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                            <td className="py-4 px-4 text-neutral-700 text-sm font-medium">{item.email}</td>
                            <td className="py-4 px-4 text-neutral-500 text-sm">
                              {item.unsubscribe_page_viewed_at
                                ? new Date(item.unsubscribe_page_viewed_at).toLocaleString()
                                : 'Not viewed'}
                            </td>
                            <td className="py-4 px-4">
                              <span className="font-semibold text-neutral-700">{item.unsubscribe_page_view_count || 0}</span>
                            </td>
                            <td className="py-4 px-4 text-neutral-500 text-sm">
                              {item.unsubscribed_at
                                ? new Date(item.unsubscribed_at).toLocaleString()
                                : '—'}
                            </td>
                            <td className="py-4 px-4 text-neutral-500 text-sm">
                              {item.reason || '—'}
                            </td>
                            <td className="py-4 px-4">
                              {(item.unsubscribed_at || isEmailUnsubscribed(item.email)) && (
                                <button
                                  onClick={() => handleResubscribe(item.email)}
                                  className="px-3 py-1.5 bg-success-600 text-white rounded-lg hover:bg-success-700 transition-all duration-200 text-xs flex items-center gap-1.5 font-semibold shadow-soft"
                                  title="Resubscribe this user"
                                >
                                  <FiRefreshCw className="w-3.5 h-3.5" />
                                  Resubscribe
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Thread Reply Modal */}
      <ThreadModal
        threadModal={threadModal}
        replyBody={replyBody}
        setReplyBody={setReplyBody}
        sendingReply={sendingReply}
        handleSendReply={handleSendReply}
        handleConfirmReply={handleConfirmReply}
        handleRecallPendingReply={handleRecallPendingReply}
        showReplyConfirm={showReplyConfirm}
        setShowReplyConfirm={setShowReplyConfirm}
        pendingReplyQueueId={pendingReplyQueueId}
        pendingReplyRecalled={pendingReplyRecalled}
        replyCountdown={replyCountdown}
        recallingReplyId={recallingReplyId}
        onClose={() => setThreadModal(null)}
        userEmail={userEmail}
        formatMsgDate={formatMsgDate}
        extractName={extractName}
        extractEmail={extractEmail}
      />
    </div>
  );
};

// ── Thread Modal Component ──────────────────────────────────────────
const ThreadModal = ({ threadModal, replyBody, setReplyBody, sendingReply, handleSendReply, handleConfirmReply, handleRecallPendingReply, showReplyConfirm, setShowReplyConfirm, pendingReplyQueueId, pendingReplyRecalled, replyCountdown, recallingReplyId, onClose, userEmail, formatMsgDate, extractName, extractEmail }) => {
  if (!threadModal) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl border border-neutral-200 w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-neutral-200 flex items-center gap-3">
          <button onClick={onClose} className="p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100"><FiArrowLeft className="w-4 h-4" /></button>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-bold text-neutral-900 truncate">{threadModal.subject}</h3>
            <p className="text-xs text-neutral-500">Thread with {threadModal.to_email}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100"><FiX className="w-4 h-4" /></button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {threadModal.loading && (
            <div className="flex items-center justify-center h-24 text-neutral-500 text-sm">
              <FiRefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading thread…
            </div>
          )}
          {!threadModal.loading && threadModal.messages.length === 0 && (
            <div className="text-center py-8 text-neutral-400 text-sm">
              <FiMail className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No replies found for this email yet.
            </div>
          )}
          {threadModal.messages.map((msg) => {
            const fromAddr = extractEmail(msg.from);
            const isMine = fromAddr === userEmail;
            const isPending = msg.pending && msg.queueId === pendingReplyQueueId && !pendingReplyRecalled;
            return (
              <div key={msg.id} className={`rounded-xl border p-4 ${
                isMine ? 'bg-primary-50 border-primary-200 ml-8' : 'bg-white border-neutral-200 mr-8'
              } ${isPending ? 'bg-amber-50 border-amber-200' : ''}`}>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-neutral-800">{isMine ? 'You' : extractName(msg.from)}</span>
                    {isPending && <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Pending</span>}
                  </div>
                  <span className="text-xs text-neutral-400 shrink-0">{formatMsgDate(msg.date)}</span>
                </div>
                <div className="prose prose-sm max-w-none text-neutral-700 overflow-x-auto text-sm" dangerouslySetInnerHTML={{ __html: msg.body }} />
                {isPending && (
                  <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="text-xs text-amber-600 font-medium">
                      ⏳ Sending in {replyCountdown}s — recall available
                    </span>
                    <button
                      onClick={handleRecallPendingReply}
                      disabled={recallingReplyId === msg.queueId}
                      className="px-3 py-1 bg-red-600 text-white text-xs font-semibold rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1 w-fit"
                    >
                      {recallingReplyId === msg.queueId ? <FiRefreshCw className="w-3 h-3 animate-spin" /> : <FiX className="w-3 h-3" />}
                      Recall reply
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Reply box */}
        {!threadModal.loading && threadModal.messages.length > 0 && (
          <div className="border-t border-neutral-200 p-4 bg-neutral-50 rounded-b-2xl">
            <form onSubmit={handleSendReply} className="flex gap-2">
              <textarea
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder="Type your reply..."
                rows={2}
                className="flex-1 resize-none border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
              <button
                type="submit"
                disabled={sendingReply || !replyBody.trim()}
                className="self-end px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition"
              >
                {sendingReply ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiSend className="w-4 h-4" />}
                Send
              </button>
            </form>
          </div>
        )}

        {/* Reply confirmation modal */}
        {showReplyConfirm && (
          <div className="absolute inset-0 z-10 bg-black/30 flex items-center justify-center p-4 rounded-2xl">
            <div className="bg-white rounded-xl shadow-xl border border-neutral-200 p-5 max-w-sm w-full">
              <h4 className="text-base font-bold text-neutral-900 mb-2">Send this reply?</h4>
              <p className="text-sm text-neutral-600 mb-4">
                The reply will be queued for 60 seconds so you can recall it if needed.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowReplyConfirm(false)}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-neutral-600 hover:bg-neutral-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmReply}
                  disabled={sendingReply}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1.5"
                >
                  {sendingReply ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiSend className="w-4 h-4" />}
                  Yes, Send Reply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackingDashboard;
